import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  Avatar,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Report,
  Warning,
  CheckCircle,
  Cancel,
  AccessTime,
  Person,
  Block
} from '@mui/icons-material';
import axios from 'axios';

interface ReportData {
  id: string;
  reporterId: string;
  reportedUserId: string;
  reporterName: string;
  reportedUserName: string;
  reporterAvatar?: string;
  reportedUserAvatar?: string;
  reason: string;
  description: string;
  status: string;
  createdAt: Date;
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [banDialog, setBanDialog] = useState<{open: boolean, userId: string, userName: string, reason: string}>({
    open: false,
    userId: '',
    userName: '',
    reason: ''
  });

  const fetchReports = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`https://api.sobcof.com/api/reports?page=${page + 1}&limit=${rowsPerPage}&status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleStatusUpdate = async (reportId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('adminToken');
              await axios.patch(`https://api.sobcof.com/api/reports/${reportId}/status`, {
        status: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchReports();
    } catch (error) {
      console.error('Error updating report status:', error);
    }
  };

  const handleBanUser = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`https://api.sobcof.com/api/users/${banDialog.userId}/ban`, {
        reason: banDialog.reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Kullanıcı başarıyla banlandı');
      setBanDialog({ open: false, userId: '', userName: '', reason: '' });
      fetchReports();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Kullanıcı banlanamadı');
    }
  };

  const openBanDialog = (userId: string, userName: string, reason: string) => {
    setBanDialog({
      open: true,
      userId,
      userName,
      reason: `Şikayet nedeni: ${reason}`
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'resolved':
        return 'Çözüldü';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Warning />;
      case 'resolved':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      default:
        return <Report />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* Enhanced App Bar */}
      <AppBar 
        position="static" 
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Report sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Şikayet Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Report sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {reports.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Toplam Şikayet
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white'
            }}>
              <Warning sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {reports.filter(r => r.status === 'pending').length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Bekleyen
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white'
            }}>
              <CheckCircle sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {reports.filter(r => r.status === 'resolved').length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Çözülen
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
              color: 'white'
            }}>
              <Cancel sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {reports.filter(r => r.status === 'rejected').length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Reddedilen
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Enhanced Filters */}
        <Card elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Report sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtreler
            </Typography>
          </Box>
          
          <FormControl size="medium" sx={{ minWidth: 200 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">Tüm Şikayetler</MenuItem>
              <MenuItem value="pending">Bekleyen</MenuItem>
              <MenuItem value="resolved">Çözülen</MenuItem>
              <MenuItem value="rejected">Reddedilen</MenuItem>
            </Select>
          </FormControl>
        </Card>

        {/* Enhanced Reports Table */}
        <Card elevation={0} sx={{ 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Şikayet ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Şikayet Eden</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Şikayet Edilen</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Sebep</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Açıklama</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Tarih</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={200} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  reports.map((report) => (
                    <TableRow 
                      key={report.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          #{report.id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            src={report.reporterAvatar}
                            sx={{ 
                              bgcolor: 'primary.main',
                              width: 35,
                              height: 35
                            }}
                          >
                            {!report.reporterAvatar && <Person sx={{ fontSize: 18 }} />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {report.reporterName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {report.reporterId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            src={report.reportedUserAvatar}
                            sx={{ 
                              bgcolor: 'secondary.main',
                              width: 35,
                              height: 35
                            }}
                          >
                            {!report.reportedUserAvatar && <Person sx={{ fontSize: 18 }} />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {report.reportedUserName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {report.reportedUserId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.reason}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 600, maxWidth: 150 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ 
                          maxWidth: 200,
                          fontWeight: 500,
                          color: 'text.secondary'
                        }}>
                          {report.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(report.status)}
                          label={getStatusLabel(report.status)}
                          size="small"
                          color={getStatusColor(report.status) as any}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(report.createdAt).toLocaleDateString('tr-TR')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {report.status === 'pending' && (
                            <>
                              <Tooltip title="Çözüldü olarak işaretle">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  onClick={() => handleStatusUpdate(report.id, 'resolved')}
                                  sx={{ 
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    px: 2
                                  }}
                                >
                                  <CheckCircle fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Reddet">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleStatusUpdate(report.id, 'rejected')}
                                  sx={{ 
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    px: 2
                                  }}
                                >
                                  <Cancel fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Şikayet Edilen Kullanıcıyı Banla">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => openBanDialog(report.reportedUserId, report.reportedUserName, report.reason)}
                                  sx={{ 
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    px: 2
                                  }}
                                >
                                  <Block fontSize="small" />
                                </Button>
                              </Tooltip>
                            </>
                          )}
                          {report.status !== 'pending' && (
                            <>
                              <Chip
                                label={report.status === 'resolved' ? 'Çözüldü' : 'Reddedildi'}
                                size="small"
                                color={report.status === 'resolved' ? 'success' : 'error'}
                                sx={{ fontWeight: 600 }}
                              />
                              <Tooltip title="Şikayet Edilen Kullanıcıyı Banla">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => openBanDialog(report.reportedUserId, report.reportedUserName, report.reason)}
                                  sx={{ 
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    px: 2
                                  }}
                                >
                                  <Block fontSize="small" />
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={reports.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{ 
              borderTop: '1px solid #e2e8f0',
              '& .MuiTablePagination-select': { borderRadius: 1 }
            }}
          />
        </Card>
      </Box>

      {/* Ban Confirmation Dialog */}
      <Dialog
        open={banDialog.open}
        onClose={() => setBanDialog({ open: false, userId: '', userName: '', reason: '' })}
        aria-labelledby="ban-dialog-title"
        aria-describedby="ban-dialog-description"
      >
        <DialogTitle id="ban-dialog-title">
          Kullanıcıyı Banla
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="ban-dialog-description">
            <strong>{banDialog.userName}</strong> kullanıcısını banlamak istediğinizden emin misiniz?
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Ban Sebebi:</strong> {banDialog.reason}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBanDialog({ open: false, userId: '', userName: '', reason: '' })}
            color="primary"
          >
            İptal
          </Button>
          <Button 
            onClick={handleBanUser}
            color="error"
            variant="contained"
            startIcon={<Block />}
          >
            Banla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;
